import { SigningSectionsNav } from './signing-sections-nav'
import { Anchor, denormalizeRect } from '@st/geom'
import { FloatingToolbar, Layer, type DocumentViewerMessage } from '@st/document-viewer-kit'
import {
  selActiveAnnotation,
  selAnnotationsPluginState
} from '@st/document-viewer-kit/plugins/annotations'
import {
  Button,
  ChevronDownIcon,
  CloseIcon,
  Dialog,
  DialogButtons,
  Toolbar,
  ToolbarSection,
  Modal,
  ZoomInIcon,
  ZoomOutIcon
} from '@st/theme'
import type { ContractField } from '@st/sdk'
import { useId, useState } from 'react'
import { match } from '@st/pattern'
import { AnnotationsOverlay } from '~/st-doc-viewer/runtime-definition'
import {
  selViewerAnnotationsByPageNumber,
  type STViewerAnnotation
} from '~/st-doc-viewer/viewer-annotations'
import { useProcess, useSelect } from '~/store'
import { ContractDocumentViewport, useContractViewer } from './contract-document-viewport'
import { selContractZoom, type ContractViewerDocument } from './contract-signing-shell-module'
import {
  selSigningDialog,
  type ActiveContractSigningState,
  type ContractSigningWizardMessage,
  type SigningDialogModel
} from './contract-signing-wizard-module'
import { PaymentRequiredNotice } from './payment-required-notice'
import { IdentityVerificationPanel } from './identity-verification-panel'

/**
 * Full-screen signing surface. Rows: a header (close, current signer, the
 * mobile Sections toggle, the phase action) and the document row, where the
 * bookmarks nav is a permanent sidebar from `lg` up and a dropdown below it.
 *
 * Phase overlays (signature capture, handoff, identity verification) cover the
 * whole surface including the header, so while one is up the only exits are
 * the overlay's own buttons and the dialog's dismiss policy.
 */
export function ContractSigningModal({
  document,
  signing,
  isArchived,
  paymentRequired,
  paymentPromptOpen
}: {
  document: ContractViewerDocument
  signing: ActiveContractSigningState
  isArchived: boolean
  paymentRequired: boolean
  paymentPromptOpen: boolean
}) {
  const contractSigning = useProcess('contractSigning')
  const sectionsId = useId()
  const [bookmarksOpen, setBookmarksOpen] = useState(false)
  const dialog = selSigningDialog(signing, isArchived)

  function sendSigning(message: ContractSigningWizardMessage) {
    contractSigning.send({
      type: 'signing',
      message,
      paymentRequired
    })
  }

  function sendAnnotationMessage(message: DocumentViewerMessage) {
    contractSigning.send({
      type: 'viewer',
      message: { type: 'viewer', message },
      paymentRequired
    })
  }

  const onDismiss = match(paymentPromptOpen)
    .with(true, () => () => contractSigning.send({ type: 'paymentRequiredClosed' }))
    .with(false, () =>
      match(dialog.dismiss)
        .with(undefined, () => undefined)
        .otherwise((message) => () => sendSigning(message))
    )
    .exhaustive()

  const actionClassName = 'min-h-11 min-w-20 sm:min-w-24'

  return (
    <Modal isOpen={true} onClose={onDismiss}>
      <div className="fixed inset-0 overflow-hidden bg-white">
        <div className="relative grid h-full min-w-0 grid-rows-[auto,minmax(0,1fr)] overflow-hidden">
          <div className="grid min-w-0 grid-cols-[minmax(0,1fr),auto,minmax(0,1fr)] items-center gap-1 border-b border-gray-200 bg-white px-2 py-2 sm:gap-3 sm:px-4">
            <div className="flex min-w-0 items-center gap-1 sm:gap-3">
              <Button
                variant="subtle"
                aria-label="Close signing"
                className="size-11 shrink-0"
                leadingIcon={<CloseIcon className="h-4 w-4" />}
                disabled={onDismiss == undefined}
                onClick={onDismiss}
              />
              {dialog.signerName && (
                <div className="truncate text-base font-medium text-gray-700">
                  {dialog.signerName}
                </div>
              )}
            </div>
            <div>
              {document.pageGroups.length > 0 && (
                <button
                  type="button"
                  aria-expanded={bookmarksOpen}
                  aria-controls={sectionsId}
                  className="group flex min-h-11 items-center justify-center gap-1 px-1 text-base font-normal text-gray-700 lg:hidden"
                  onClick={() => setBookmarksOpen(!bookmarksOpen)}
                >
                  Sections
                  <ChevronDownIcon className="size-3.5 group-aria-expanded:rotate-180" />
                </button>
              )}
            </div>
            <div className="flex min-h-11 justify-self-end">
              {match(dialog.action)
                .with(undefined, () => null)
                .with({ type: 'waiting' }, ({ label }) => (
                  <Button className={actionClassName} disabled>
                    {label}
                  </Button>
                ))
                .with({ type: 'act' }, ({ label, message, enabled }) => (
                  <Button
                    variant="primary"
                    className={actionClassName}
                    disabled={!enabled}
                    onClick={() => sendSigning(message)}
                  >
                    {label}
                  </Button>
                ))
                .exhaustive()}
            </div>
          </div>
          <div className="relative flex min-h-0 min-w-0 overflow-hidden">
            <SigningSectionsNav
              id={sectionsId}
              pageGroups={document.pageGroups}
              isOpen={bookmarksOpen}
              onClose={() => setBookmarksOpen(false)}
              onSelect={(bookmark) =>
                contractSigning.send({
                  type: 'bookmarkSelected',
                  pageNumber: bookmark.pageRange.first
                })
              }
            />
            <div className="relative flex min-h-0 min-w-0 flex-1 flex-col bg-stone-500">
              <ZoomToolbar />
              <div className="min-h-0 flex-1">
                <SigningDocument
                  document={document}
                  signHereField={dialog.signHereField}
                  onSignHere={() => sendSigning({ type: 'signatureCaptureOpened' })}
                  onAnnotationMessage={sendAnnotationMessage}
                />
              </div>
            </div>
          </div>

          {match(dialog.overlay)
            .with(undefined, () => null)
            .with({ type: 'handoff' }, ({ nextSignerName, enabled }) => (
              <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-stone-500">
                <Dialog
                  title="Your part is complete"
                  subtitle={`Your signatures are saved. ${nextSignerName} still needs to review and sign.`}
                  buttons={
                    <DialogButtons>
                      <Button
                        variant="subtle"
                        disabled={!enabled}
                        onClick={() => sendSigning({ type: 'finishLaterClicked' })}
                      >
                        Finish later
                      </Button>
                      <Button
                        variant="primary"
                        disabled={!enabled}
                        onClick={() => sendSigning({ type: 'continueAsNextSigner' })}
                      >
                        Continue as {nextSignerName}
                      </Button>
                    </DialogButtons>
                  }
                />
              </div>
            ))
            .with({ type: 'verification' }, ({ verification, signaturesSaved }) => (
              <IdentityVerificationPanel
                state={verification}
                signaturesSaved={signaturesSaved}
                isArchived={isArchived}
                onMessage={(message) => sendSigning({ type: 'verification', message })}
                onClose={() => sendSigning({ type: 'closed' })}
              />
            ))
            .with({ type: 'capture' }, (capture) => (
              <SignatureCapturePanel
                capture={capture}
                onClose={() => sendSigning({ type: 'signatureCaptureClosed' })}
                onSign={(value) => sendSigning({ type: 'signatureCaptured', value })}
              />
            ))
            .exhaustive()}
          {paymentPromptOpen && (
            <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-stone-500">
              <PaymentRequiredNotice
                onClose={() => contractSigning.send({ type: 'paymentRequiredClosed' })}
                onGoToDashboard={() => sendSigning({ type: 'closed' })}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

// The document toolbar from `lg` up; below that it floats over the top right
// of the PDF so it stays put while the document scrolls.
function ZoomToolbar() {
  const contractSigning = useProcess('contractSigning')
  const zoom = useSelect('contractSigning', selContractZoom)
  return (
    <Toolbar className="absolute right-2 top-2 z-10 shrink-0 rounded border bg-white shadow-md max-lg:border-stone-600 max-lg:bg-stone-700 max-lg:p-1 lg:static lg:rounded-none lg:border-r-0 lg:border-t-0 lg:shadow-none">
      <ToolbarSection className="max-lg:flex-col-reverse max-lg:gap-0">
        <Button
          variant="subtle"
          className="max-lg:size-11 max-lg:text-white max-lg:hover:bg-stone-600 max-lg:active:bg-stone-500"
          aria-label="Zoom out"
          disabled={zoom.zoomOut == undefined}
          onClick={() => contractSigning.send({ type: 'zoomClicked', direction: 'out' })}
        >
          <ZoomOutIcon className="size-6" />
        </Button>
        <Button
          variant="subtle"
          className="max-lg:size-11 max-lg:text-white max-lg:hover:bg-stone-600 max-lg:active:bg-stone-500"
          aria-label="Zoom in"
          disabled={zoom.zoomIn == undefined}
          onClick={() => contractSigning.send({ type: 'zoomClicked', direction: 'in' })}
        >
          <ZoomInIcon className="size-6" />
        </Button>
      </ToolbarSection>
    </Toolbar>
  )
}

/** The source PDF with signature annotations and the "Sign here" flag on the guided field. */
function SigningDocument({
  document,
  signHereField,
  onSignHere,
  onAnnotationMessage
}: {
  document: ContractViewerDocument
  signHereField: ContractField | undefined
  onSignHere: () => void
  onAnnotationMessage: (message: DocumentViewerMessage) => void
}) {
  const { viewerState } = useContractViewer()
  const annotationState = selAnnotationsPluginState(viewerState)
  const activeAnnotation = selActiveAnnotation(viewerState) as STViewerAnnotation | undefined
  const annotationsByPageNumber = selViewerAnnotationsByPageNumber(viewerState)

  return (
    <ContractDocumentViewport
      document={document}
      renderPage={(page) => (
        <Layer>
          <AnnotationsOverlay
            send={onAnnotationMessage}
            page={page}
            annotations={annotationsByPageNumber[page.pageNumber] ?? []}
            annotationState={annotationState}
            activeAnnotation={activeAnnotation}
            renderToolOverlay={true}
          />
          {signHereField && signHereField.page == page.pageNumber && (
            <FloatingToolbar
              target={denormalizeRect(signHereField.rect, page.bounds)}
              targetAnchor={Anchor.centerLeft}
              toolbarAnchor={Anchor.centerRight}
              toolbarOffset={{ dx: -8, dy: 0 }}
            >
              <button
                className="whitespace-nowrap bg-yellow-300 py-2 pl-3 pr-5 text-base font-semibold text-gray-900"
                style={{ clipPath: 'polygon(0 0, 85% 0, 100% 50%, 85% 100%, 0 100%)' }}
                onClick={onSignHere}
              >
                Sign here
              </button>
            </FloatingToolbar>
          )}
        </Layer>
      )}
    />
  )
}

/** The typed signature is an input draft: local until the signer confirms it. */
function SignatureCapturePanel({
  capture,
  onClose,
  onSign
}: {
  capture: Extract<SigningDialogModel['overlay'], { type: 'capture' }>
  onClose: () => void
  onSign: (value: string) => void
}) {
  const [signature, setSignature] = useState(capture.initialValue)
  const canSign = capture.enabled && signature.trim() != ''

  return (
    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-stone-500">
      <Dialog
        title={capture.title}
        subtitle={capture.fieldLabel}
        buttons={
          <DialogButtons>
            <Button variant="subtle" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              aria-label={`Confirm signature for ${capture.fieldLabel}`}
              disabled={!canSign}
              onClick={() => onSign(signature)}
            >
              Sign
            </Button>
          </DialogButtons>
        }
      >
        <div className="rounded-3xl border border-gray-200 bg-stone-50 p-6 shadow-inner">
          <label className="text-sm font-medium uppercase tracking-wide text-gray-500">
            {capture.inputLabel}
          </label>
          <input
            autoFocus
            className="font-signature mt-6 w-full border-0 bg-transparent text-4xl text-gray-700 outline-none ring-0 placeholder:font-sans placeholder:text-gray-400 focus:border-transparent focus:outline-none focus:ring-0 focus-visible:border-transparent focus-visible:outline-none focus-visible:ring-0"
            aria-label={`Typed signature for ${capture.fieldLabel}`}
            value={signature}
            placeholder="Your Name"
            disabled={!capture.enabled}
            onChange={(event) => setSignature(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key == 'Enter' && canSign) onSign(signature)
            }}
            data-lpignore="true"
            data-1p-ignore="true"
          />
          <div className="mt-4 border-b border-gray-300" />
        </div>
      </Dialog>
    </div>
  )
}
